import { Box, Group, Stack, TextInput } from '@mantine/core';
import { useState } from 'react';
import { exportGoalsCSV } from '../../../api/portfolio-client/portfolio-client';
import { useGoals } from '../../../api/strategy-client/strategy-client.hooks';
import type { Goal } from '../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import {
  setSelectedGoal,
  setTableSortBy,
  setTableSortDirection,
} from '../../../store/strategy-store/strategy-store.actions';
import { baseWhite, inkLight } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { StrategyEntityTable } from '../components/strategy-entity-table';
import { MainSectionExportActions } from '../initiative-list/main-section/main-section-export-actions';
import { GoalPresentationModal } from './presentation-modal/presentation-modal';

type Props = {
  isLoading: boolean;
};

export function GoalsMainSection({ isLoading: _isLoading }: Props) {
  const i18n = useTranslation();
  const { goals } = useGoals();
  const teams = useGlobalStore((state) => state.teams);
  const organization = useGlobalStore((state) => state.organization);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const selectedGoal = useStrategyStore((state) => state.selectedGoal);
  const tableSortBy = useStrategyStore((state) => state.tableSortBy);
  const tableSortDirection = useStrategyStore((state) => state.tableSortDirection);
  const statusFilters = useStrategyStore((state) => state.filters.status);
  const onlyBookmarkedFilter = useStrategyStore((state) => state.filters.only_bookmarked);
  const includeEmptyFilter = useStrategyStore((state) => state.filters.include_empty);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [presentationModalOpen, setPresentationModalOpen] = useState(false);

  // Filter goals based on search, status, bookmark, and include empty
  const filteredGoals = goals.filter((goal) => {
    // Include empty filter (show only goals with outcomes when OFF)
    if (!includeEmptyFilter) {
      if (!goal.outcomes || goal.outcomes.length === 0) {
        return false;
      }
    }

    // Search filter
    const matchesSearch =
      goal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (goal.key && goal.key.toLowerCase().includes(searchTerm.toLowerCase()));

    // Status filter
    const goalStatus = goal.status?.by_tasks?.status || 'unknown';
    const matchesStatus = statusFilters.length === 0 || statusFilters.includes(goalStatus);

    // Bookmark filter
    const matchesBookmark = !onlyBookmarkedFilter || goal.is_bookmarked;

    return matchesSearch && matchesStatus && matchesBookmark;
  });

  const handleSortChange = (sortBy: string, direction: 'asc' | 'desc') => {
    setTableSortBy(sortBy as any);
    setTableSortDirection(direction as any);
  };

  const handleExportCSV = async () => {
    if (!organization?.id) return;
    try {
      const goalIds = filteredGoals.map((goal) => goal.id);
      await exportGoalsCSV(organization.id, portfolioId, goalIds);
    } catch (error) {
      console.error('Failed to export goals CSV:', error);
    }
  };

  return (
    <Stack h="100%" style={{ overflow: 'hidden' }} gap="xs">
      <Group justify="space-between" miw={760}>
        <Group gap={16}>
          <Button
            variant="secondary"
            radius="xl"
            size="sm"
            leftSection={<Icon name="format_list_bulleted" color={baseWhite} size={16} style={{ cursor: 'pointer' }} />}
          >
            Table View
          </Button>
        </Group>
        <Group>
          <TextInput
            placeholder={`Search by ${i18n.t('common.strategic_goal').toLowerCase()} name or ID`}
            radius="xl"
            size="sm"
            value={searchTerm}
            leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
            style={{ width: 300 }}
            onChange={(event) => setSearchTerm(event.currentTarget.value)}
          />
          <MainSectionExportActions
            disabled={filteredGoals.length === 0}
            onExportCSV={handleExportCSV}
            onCreatePresentation={() => setPresentationModalOpen(true)}
            canExportPresentations={!!organization?.can_export_presentations}
          />
        </Group>
      </Group>

      <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <StrategyEntityTable<Goal>
          entities={filteredGoals}
          entityType="goal"
          onEntityClick={setSelectedGoal}
          selectedEntityId={selectedGoal?.id}
          page={currentPage}
          onPageChange={setCurrentPage}
          tableSortBy={tableSortBy}
          tableSortDirection={tableSortDirection}
          onSortChange={handleSortChange}
          showTeamsColumn={false}
          teams={teams}
          filtersApplied={true}
        />
      </Box>

      {organization?.can_export_presentations && (
        <GoalPresentationModal
          opened={presentationModalOpen}
          onClose={() => setPresentationModalOpen(false)}
          goalIds={filteredGoals.map((goal) => goal.id)}
          goalsCount={filteredGoals.length}
        />
      )}
    </Stack>
  );
}
